import moment from 'moment';
import { isEmpty } from 'lodash';
import { createReducer } from '../../../helpers/reducer';
import { getCurrentCountryContext } from '../../../helpers/countriesHelpers';
import { minMultiplierValue } from './constans';
import actions from './actions';
import { t } from '../../../i18n';

const initState = {
  holidays: [],
  editForm: [],
  districts: [],
  isDistrictsLoading: false,
  isHolidayPricingModalVisible: false,
  isConfirmRemovalModalVisible: false,
  isCreateHolidayPricingLoading: false,
  isUpdateHolidayPricingLoading: false,
  isRemoveHolidayPricingLoading: false,
  selectedDate: null,
  selectedRecord: null,
  loadingIndex: null,
};

function validate(attr, value, index, state) {
  const countryCode = getCurrentCountryContext().code;
  const holidayPricingForm = state.editForm?.[index];

  const startsAtValue = attr === 'startsAt' ? value : holidayPricingForm?.startsAt?.value;
  const finishesAtValue = attr === 'finishesAt' ? value : holidayPricingForm?.finishesAt?.value;

  let error = '';

  if (attr === 'multiplier') {
    if (!value || value < minMultiplierValue[countryCode]) {
      error = t('dash.validation.greater_or_equal_error', { value: minMultiplierValue[countryCode] });
    }
  }

  if (attr === 'startsAt') {
    if (isEmpty(startsAtValue) && !isEmpty(finishesAtValue)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.holidayPricing.start_time_label') });
    } else if (finishesAtValue && moment(startsAtValue, 'HH:mm a').isAfter(moment(finishesAtValue, 'HH:mm a'))) {
      error = t('dash.validation.mast_be_before_error', {
        firstFieldName: t('dash.admin.holidayPricing.start_time_label'),
        secondFieldName: t('dash.admin.holidayPricing.end_time_label'),
      });
    }
  }

  if (attr === 'finishesAt') {
    if (isEmpty(finishesAtValue) && !isEmpty(startsAtValue)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.holidayPricing.end_time_label') });
    } else if (startsAtValue && moment(finishesAtValue, 'HH:mm a').isBefore(moment(startsAtValue, 'HH:mm a'))) {
      error = t('dash.validation.mast_be_after_error', {
        firstFieldName: t('dash.admin.holidayPricing.end_time_label'),
        secondFieldName: t('dash.admin.holidayPricing.start_time_label'),
      });
    }
  }

  return error;
}

function onClearModalData(state) {
  return {
    ...state,
    editForm: [],
    selectedDate: null,
    selectedRecord: null,
    toggleConfirmRemovalModal: false,
    loadingIndex: null,
  };
}

function onSetSelectedDate(state, { payload }) {
  return {
    ...state,
    selectedDate: payload,
  };
}

function onSetSelectedRecord(state, { payload }) {
  const { selectedRecord, form } = payload;

  return {
    ...state,
    selectedRecord,
    editForm: form,
  };
}

function onRemoveHolidayPricingRequest(state) {
  return {
    ...state,
    isRemoveHolidayPricingLoading: true,
  };
}

function onRemoveHolidayPricingSuccess(state) {
  return {
    ...state,
    isRemoveHolidayPricingLoading: false,
  };
}

function onRemoveHolidayPricingError(state) {
  return {
    ...state,
    isRemoveHolidayPricingLoading: false,
  };
}

function onCreateHolidayPricingRequest(state) {
  return {
    ...state,
    isCreateHolidayPricingLoading: true,
  };
}

function onCreateHolidayPricingSuccess(state) {
  return {
    ...state,
    isCreateHolidayPricingLoading: false,
    loadingIndex: null,
  };
}

function onCreateHolidayPricingError(state) {
  return {
    ...state,
    isCreateHolidayPricingLoading: false,
    loadingIndex: null,
  };
}

function onUpdateHolidayPricingRequest(state) {
  return {
    ...state,
    isUpdateHolidayPricingLoading: true,
  };
}

function onUpdateHolidayPricingSuccess(state) {
  return {
    ...state,
    isUpdateHolidayPricingLoading: false,
    loadingIndex: null,
  };
}

function onUpdateHolidayPricingError(state) {
  return {
    ...state,
    isUpdateHolidayPricingLoading: false,
    loadingIndex: null,
  };
}

function onLoadDistrictsRequest(state) {
  return {
    ...state,
    isDistrictsLoading: true,
  };
}

function onLoadDistrictsSuccess(state, { payload }) {
  const {
    data: { districts },
  } = payload;

  return {
    ...state,
    isDistrictsLoading: false,
    districts,
  };
}

function onLoadHolidaysRequest(state) {
  return {
    ...state,
    isHolidaysLoading: true,
  };
}

function onLoadHolidaysSuccess(state, { payload }) {
  return {
    ...state,
    holidays: payload,
    isHolidaysLoading: false,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value, index } = payload;
  const { editForm } = state;

  editForm[index][attr] = {
    value,
    error: validate(attr, value, index, state),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: [...editForm],
  };
}

function onClearStore() {
  return { ...initState };
}

function onToggleHolidayPricingModal(state, { payload }) {
  return {
    ...state,
    isHolidayPricingModalVisible: payload,
  };
}

function onToggleConfirmRemovalModal(state, { payload }) {
  return {
    ...state,
    isConfirmRemovalModalVisible: payload,
  };
}

function onSetLoadingIndex(state, { payload }) {
  return {
    ...state,
    loadingIndex: payload,
  };
}

function onAddFormItem(state) {
  return {
    ...state,
    editForm: [...state.editForm, {}],
  };
}

function onRemoveFormItem(state, { payload }) {
  const { index } = payload;

  if (state?.editForm?.length > 1) {
    const updatedForm = state.editForm.filter((_, i) => i !== index);

    return {
      ...state,
      editForm: updatedForm,
    };
  }

  return {
    ...state,
  };
}

export default createReducer(initState, {
  [actions.ADD_FORM_ITEM]: onAddFormItem,
  [actions.REMOVE_FORM_ITEM]: onRemoveFormItem,
  [actions.LOAD_DISTRICTS_REQUEST]: onLoadDistrictsRequest,
  [actions.LOAD_DISTRICTS_SUCCESS]: onLoadDistrictsSuccess,
  [actions.LOAD_HOLIDAYS_REQUEST]: onLoadHolidaysRequest,
  [actions.LOAD_HOLIDAYS_SUCCESS]: onLoadHolidaysSuccess,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.TOGGLE_HOLIDAY_PRICING_MODAL]: onToggleHolidayPricingModal,
  [actions.CREATE_HOLIDAY_PRICING_REQUEST]: onCreateHolidayPricingRequest,
  [actions.CREATE_HOLIDAY_PRICING_SUCCESS]: onCreateHolidayPricingSuccess,
  [actions.CREATE_HOLIDAY_PRICING_ERROR]: onCreateHolidayPricingError,
  [actions.UPDATE_HOLIDAY_PRICING_REQUEST]: onUpdateHolidayPricingRequest,
  [actions.UPDATE_HOLIDAY_PRICING_SUCCESS]: onUpdateHolidayPricingSuccess,
  [actions.UPDATE_HOLIDAY_PRICING_ERROR]: onUpdateHolidayPricingError,
  [actions.REMOVE_HOLIDAY_PRICING_REQUEST]: onRemoveHolidayPricingRequest,
  [actions.REMOVE_HOLIDAY_PRICING_SUCCESS]: onRemoveHolidayPricingSuccess,
  [actions.REMOVE_HOLIDAY_PRICING_ERROR]: onRemoveHolidayPricingError,
  [actions.SET_SELECTED_DATE]: onSetSelectedDate,
  [actions.SET_SELECTED_RECORD]: onSetSelectedRecord,
  [actions.CLEAR_MODAL_DATA]: onClearModalData,
  [actions.TOGGLE_CONFIRM_REMOVAL_MODAL]: onToggleConfirmRemovalModal,
  [actions.SET_LOADING_INDEX]: onSetLoadingIndex,
});
